import type { AnyDomainEvent } from '@abastece/shared';
import { orderKey } from '@abastece/shared';
import type { ApplyResult, Reader } from '../state.js';

export async function reduceDispositivo(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'dispositivo.registrado': {
      const { dispositivoId, codigo, nombre, tipo } = event.payload;

      /**
       * Two devices registered offline could pick the same code, and a duplicate code means
       * duplicate ticket numbers — two different receipts reading A1-000042. Registration is
       * therefore the one operation that must happen online, and this check is the backstop
       * for the case where it somehow did not.
       */
      const clash = await read.dispositivoPorCodigo(codigo);
      if (clash && clash.id !== dispositivoId) {
        return {
          effects: [],
          rejections: [
            { eventId: event.id, reason: `El código ${codigo} ya está en uso por otro dispositivo` },
          ],
        };
      }

      return {
        effects: [
          {
            kind: 'dispositivo.upsert',
            id: dispositivoId,
            // Whoever registered it is using it until somebody signs in on it.
            row: { id: dispositivoId, codigo, nombre, tipo, activo: true, usuarioActual: event.actorId, rev },
          },
        ],
        rejections: [],
      };
    }

    case 'dispositivo.revocado':
      return {
        effects: [
          {
            kind: 'dispositivo.patch',
            id: event.payload.dispositivoId,
            rev,
            patch: { activo: false },
          },
        ],
        rejections: [],
      };

    // Given back: the server takes its token again (`requireDevice` looks at `activo`, and the
    // token itself was never cancelled), and the device picks up where it stopped.
    case 'dispositivo.reactivado':
      return {
        effects: [
          {
            kind: 'dispositivo.patch',
            id: event.payload.dispositivoId,
            rev,
            patch: { activo: true },
          },
        ],
        rejections: [],
      };

    default:
      return null;
  }
}
